import { ITreatmentDebtItem, ICreatePaymentItem } from "../services/types/utils.types";

/**
 * Distributes a payment amount across debt treatments.
 * 
 * Cases handled:
 * 1. Amount < total debt: Distribute from oldest debt, covering as many treatments as possible
 * 2. Amount = total debt: Distribute exactly to cover all treatments
 * 3. Amount > total debt: Cover all treatments + add overpayment item ("יתרת זכות")
 * 
 * @param treatments - Array of debt treatment items (should be sorted oldest first)
 * @param paymentAmount - The total amount being paid
 * @returns Array of payment items ready for the API request
 */
export function distributePaymentAcrossTreatments(
  treatments: ITreatmentDebtItem[],
  paymentAmount: number
): ICreatePaymentItem[] {
  const items: ICreatePaymentItem[] = [];
  let remainingPayment = paymentAmount;

  // Calculate total debt
  const totalDebt = treatments.reduce((sum, t) => sum + t.debt, 0);

  // Iterate through treatments (assumed sorted oldest first by the API)
  for (const treatment of treatments) {
    if (remainingPayment <= 0) break;

    const debtForThisTreatment = treatment.debt;
    if (debtForThisTreatment <= 0) continue;

    // Determine how much to allocate to this treatment
    const allocationAmount = Math.min(remainingPayment, debtForThisTreatment);
    remainingPayment -= allocationAmount;

    // Format date if available
    const formattedDate = treatment.qcsu.timestamp
      ? formatDateForPayment(treatment?.appointmentDate ?? treatment.qcsu.timestamp)
      : null;

    // Get therapist name
    const therapistName = getTherapistName(treatment);

    items.push({
      CompanyServicesId: treatment.qcsu.companyServiceId ?? null,
      ProductDesc: treatment.companyServiceName ?? undefined,
      Date: formattedDate,
      TherapistName: therapistName,
      QuestionnaireCompaniesScoreUsersId: treatment.qcsu.id,
      UnitCost: allocationAmount,
    });
  }

  // If payment exceeds total debt, add overpayment item
  if (paymentAmount > totalDebt && totalDebt >= 0) {
    const overpaymentAmount = paymentAmount - totalDebt;
    items.push({
      CompanyServicesId: null,
      ProductDesc: "יתרת זכות",
      Date: null,
      TherapistName: null,
      QuestionnaireCompaniesScoreUsersId: null,
      UnitCost: overpaymentAmount,
    });
  }

  return items;
}

/**
 * Validates that the sum of item costs equals the payment amount
 */
export function validatePaymentItems(
  items: ICreatePaymentItem[],
  paymentAmount: number
): { isValid: boolean; itemsTotal: number; difference: number } {
  const itemsTotal = items.reduce((sum, item) => sum + item.UnitCost, 0);
  // Use small epsilon for floating point comparison
  const difference = Math.abs(itemsTotal - paymentAmount);
  const isValid = difference < 0.01;

  return { isValid, itemsTotal, difference };
}

/**
 * Format date from ISO string to DD/MM/YYYY format expected by backend
 */
function formatDateForPayment(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Get therapist full name from treatment item
 */
function getTherapistName(treatment: ITreatmentDebtItem): string | null {
  return treatment.therapistName || null;
}

/**
 * Calculate total debt from treatments array
 */
export function calculateTotalDebt(treatments: ITreatmentDebtItem[]): number {
  return treatments.reduce((sum, t) => sum + t.debt, 0);
}
